import { useCallback, useEffect } from "react";
import { useNavigationInterceptorContext } from "../contexts/useNavigationInterceptorContext";
import { useFreelancerContext } from "../contexts/useFreelancerContext";
import { FreelancerOnboardingFormStep } from "../models/enums/FreelancerOnboardingFormStep";

/**
 * Hook that automatically blocks navigation when there are unsaved changes in the onboarding
 */
export const useFreelancerOnboardingFormsUnsavedChanges = () => {
	const { blockNavigation } = useNavigationInterceptorContext();
	const {
		currentOnboardingFormStep: currentOnboardingStep,
		freelancerOnboardingMyPersonalInformationStepViewModel,
		freelancerOnboardingMyProfessionalInformationStepViewModel,
		freelancerOnboardingMyAvailabilityStepViewModel,
		freelancerOnboardingMyPresentationStepViewModel,
		freelancerOnboardingMySectorsStepViewModel,
		freelancerOnboardingMySkillsStepViewModel,
		freelancerOnboardingMyExpertiseStepViewModel
	} = useFreelancerContext();

	const hasUnsavedChanges = useCallback(() => {
		switch (currentOnboardingStep) {
			case FreelancerOnboardingFormStep.MyProfile:
				return false;
			case FreelancerOnboardingFormStep.MyExpertise:
				return freelancerOnboardingMyExpertiseStepViewModel?.form?.isDirty ?? false;
			case FreelancerOnboardingFormStep.MySkills:
				return freelancerOnboardingMySkillsStepViewModel?.form?.isDirty ?? false;
			case FreelancerOnboardingFormStep.MySectors:
				return freelancerOnboardingMySectorsStepViewModel?.form?.isDirty ?? false;
			case FreelancerOnboardingFormStep.MyPresentation:
				return freelancerOnboardingMyPresentationStepViewModel?.form?.isDirty ?? false;
			case FreelancerOnboardingFormStep.MyAvailability:
				return freelancerOnboardingMyAvailabilityStepViewModel?.form?.isDirty ?? false;
			case FreelancerOnboardingFormStep.MyProfessionalInformation:
				return (
					freelancerOnboardingMyProfessionalInformationStepViewModel?.form?.isDirty ??
					false
				);
			case FreelancerOnboardingFormStep.MyPersonalInformation:
				return (
					freelancerOnboardingMyPersonalInformationStepViewModel?.form?.isDirty ?? false
				);
			default:
				return false;
		}
	}, [
		currentOnboardingStep,
		freelancerOnboardingMyPersonalInformationStepViewModel,
		freelancerOnboardingMyProfessionalInformationStepViewModel,
		freelancerOnboardingMyAvailabilityStepViewModel,
		freelancerOnboardingMyPresentationStepViewModel,
		freelancerOnboardingMySectorsStepViewModel,
		freelancerOnboardingMySkillsStepViewModel,
		freelancerOnboardingMyExpertiseStepViewModel
	]);

	useEffect(() => {
		// Block navigation whenever there are unsaved changes
		blockNavigation(hasUnsavedChanges());
	}, [hasUnsavedChanges, blockNavigation]);

	return {
		hasUnsavedChanges: hasUnsavedChanges()
	};
};
